import React, { useMemo } from 'react'
import { TextField as MuiTextField } from '@mui/material'
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import type { FieldErrors } from 'react-hook-form'
import IconButton from '@mui/material/IconButton'
import CancelIcon from '@mui/icons-material/Cancel'
import { merge } from 'lodash'
import useFormField from '../../hooks/useFormField'
import { RFHControl } from '../../types'

export type TextFieldProps = {
	name?: string
	control?: RFHControl
	errors?: FieldErrors
	onClear?: () => void
	label?: string
	/**
	 * Whether the TextField should be controlled by `react-hook-form`.
	 * When `true`, uses Controller wrapper. When `false`, renders as uncontrolled component.
	 * Defaults to `true`.
	 * */
	controlled?: boolean
	showPlaceholder?: boolean
} & Omit<MuiTextFieldProps, 'label'>

const TextField = ({
	variant = 'standard',
	placeholder,
	label,
	control: propsControl,
	required,
	name,
	errors: propsErrors,
	onChange,
	onClear,
	value,
	InputProps,
	helperText,
	showPlaceholder = true,
	controlled = true,
	...rest
}: TextFieldProps) => {
	const formContext = useFormContext()
	const control = propsControl ?? formContext?.control
	const errors = propsErrors ?? formContext?.formState.errors

	const { getError } = useFormField()
	const error = getError(name, errors)
	const placeholderTxt = showPlaceholder
		? placeholder || label
		: undefined
	const labelTxt = required && label ? `${label} *` : label

	const clearEndAdornment = useMemo(
		() => (
			<div className="pr-2">
				<IconButton edge="end" onClick={onClear} size="small">
					<CancelIcon fontSize="small" />
				</IconButton>
			</div>
		),
		[onClear]
	)

	const inputProps = useMemo(() => {
		return onClear && value
			? merge({ endAdornment: clearEndAdornment }, InputProps)
			: InputProps
	}, [InputProps, clearEndAdornment, onClear, value])

	const helperTextOrPlaceholder = helperText ?? ' '

	if (name && control && controlled) {
		return (
			<Controller
				name={name}
				control={control}
				render={({ field }) => (
					<MuiTextField
						variant={variant}
						label={labelTxt}
						placeholder={placeholderTxt}
						error={!!error}
						helperText={
							error ? error.message : helperTextOrPlaceholder
						}
						{...field}
						onChange={(e) => {
							if (onChange) {
								onChange(e)
							}
							field.onChange(e)
						}}
						InputProps={inputProps}
						{...rest}
					/>
				)}
			/>
		)
	}

	return (
		<MuiTextField
			name={name}
			variant={variant}
			label={labelTxt}
			placeholder={placeholderTxt}
			error={!!error}
			helperText={error ? error.message : helperTextOrPlaceholder}
			onChange={onChange}
			value={value}
			InputProps={inputProps}
			{...rest}
		/>
	)
}

export default TextField
